/**
 * CptCombobox — searchable CPT picker (Feb 2026).
 *
 * Drop-in replacement for the legacy native `<Select>` blocks used for
 * choosing a CPT code on appointments, claims, and notes. Uses shadcn
 * `Command` + `Popover` so the user can type to filter by code, display
 * name, description, or category — much faster than scrolling a long
 * dropdown.
 *
 * Props
 * ─────
 *  - codes:      Array of cpt_code records (already provider-filtered by caller).
 *  - value:      Currently selected CPT id (string) OR the legacy combined
 *                "Display Name - Description" string (back-compat with the
 *                existing AppointmentScheduleDialog wire format).
 *  - valueMode:  'id' (default) or 'legacy-label' — controls what onChange emits.
 *  - onChange:   (newValue, fullCptObject) => void
 *  - placeholder
 *  - disabled
 *  - testId:     stable data-testid prefix (button + input)
 *  - allowClear: shows a "Clear selection" row at the top of the list
 *
 * The component is intentionally lightweight — no fetching, no state
 * for the codes list, no caching. Callers own the data shape.
 */

import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const legacyLabelFor = (c) =>
  `${c.display_name || c.code} - ${c.description}`;

export const CptCombobox = ({
  codes = [],
  value = '',
  valueMode = 'id',
  onChange,
  placeholder = 'Select CPT code',
  disabled = false,
  testId = 'cpt-combobox',
  allowClear = false,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => {
    if (!value) return null;
    if (valueMode === 'id') {
      return codes.find((c) => c.id === value) || null;
    }
    return codes.find((c) => legacyLabelFor(c) === value) || null;
  }, [codes, value, valueMode]);

  const triggerLabel = selected
    ? `${selected.display_name || selected.code}${selected.description ? ` — ${selected.description}` : ''}`
    : placeholder;

  const emit = (cpt) => {
    if (!onChange) return;
    if (!cpt) {
      onChange('', null);
      return;
    }
    if (valueMode === 'id') onChange(cpt.id, cpt);
    else onChange(legacyLabelFor(cpt), cpt);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal h-9 px-3',
            !selected && 'text-muted-foreground',
            className,
          )}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // High z-index so we always paint above any parent Dialog
        // (Radix Dialog uses z-50 — the user's report of an empty
        // dropdown on the Create Note from Template dialog had this
        // as a plausible culprit; explicit z-index removes the doubt).
        className="p-0 w-[--radix-popover-trigger-width] min-w-[320px] z-[60]"
        align="start"
      >
        <Command
          // shouldFilter=false when no search — we still want every
          // item to render even if their `value` strings somehow
          // collide.  When the user types, cmdk's default substring
          // filter takes over (cmdk 1.x lowercases internally).
          shouldFilter
        >
          <CommandInput
            placeholder="Search code, name, description…"
            data-testid={`${testId}-search`}
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>
              {(codes || []).length === 0
                ? 'No CPT codes available for this provider. Pick another provider or clear the Provider field.'
                : 'No matching CPT codes.'}
            </CommandEmpty>
            <CommandGroup>
              {allowClear && selected && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    emit(null);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-clear`}
                  className="text-gray-500"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear selection
                </CommandItem>
              )}
              {codes.map((c) => {
                const searchable = [
                  c.code,
                  c.display_name,
                  c.description,
                  c.category,
                  c.billing_type,
                ]
                  .filter(Boolean)
                  .join(' ');
                const isSel = selected && selected.id === c.id;
                return (
                  <CommandItem
                    key={c.id}
                    value={searchable}
                    onSelect={() => {
                      emit(c);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-option-${c.code}`}
                    className="flex items-start gap-2"
                  >
                    <Check
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        isSel ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold">{c.code}</span>
                        {c.display_name && c.display_name !== c.code && (
                          <span className="text-sm">{c.display_name}</span>
                        )}
                        {c.is_group_session && (
                          <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1">
                            group
                          </span>
                        )}
                        {(c.minutes_per_unit || c.duration_minutes) && (
                          <span className="text-[10px] text-gray-500 ml-auto">
                            {c.minutes_per_unit || c.duration_minutes}min/unit
                          </span>
                        )}
                      </div>
                      {c.description && (
                        <div className="text-xs text-gray-500 truncate">{c.description}</div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CptCombobox;
